import { useTranslate } from 'src/locales';
import { ReusableTable } from './table/ReusableTable';
import type { TableHeadCellProps } from 'src/components/table';
import { useMemo } from 'react';
import type { IBaseInvoice } from 'src/types/invoices';
import { InvoicesGridRow } from './table/InvoicesGridRow';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { useGetPaginatedInvoices } from './useGetPaginatedInvoices';

type Props = {
  clientId: string;
};

export function InvoicesTab({ clientId }: Props) {
  const { currentLang, t } = useTranslate();

  const { data, totalItems, totalPages, isFetching, isLoading, page, sorting, dispatch } =
    useGetPaginatedInvoices(clientId);
  const router = useRouter();

  const tableHeadColumns: TableHeadCellProps[] = useMemo(() => {
    return [
      {
        id: 'customerName',
        label: t('tables.columns.customer'),
        width: '14%',
        dataTestId: 'th-customer',
      },
      {
        id: 'invoiceNo',
        sortType: 'string',
        label: t('tables.columns.invoiceID'),
        width: '14%',
        dataTestId: 'th-invoice-id',
      },
      {
        id: 'dueDate',
        label: t('tables.columns.dueDate'),
        width: '14%',
        dataTestId: 'th-due-date',
      },
      {
        id: 'countryCode',
        label: t('tables.columns.country'),
        width: '14%',
        dataTestId: 'th-country',
      },
      {
        id: 'totalRemainingBalance',
        label: t('tables.columns.trb'),
        align: 'right',
        width: '14%',
        dataTestId: 'th-total-remaining-balance',
      },
      {
        id: 'stage',
        label: t('tables.columns.stage'),
        align: 'center',
        width: '8%',
        dataTestId: 'th-stage',
      },
      {
        id: 'status',
        label: t('tables.columns.status'),
        align: 'center',
        width: '8%',
        dataTestId: 'th-status',
      },
      {
        id: 'paymentStatus',
        label: t('tables.columns.paymentStatus'),
        align: 'center',
        width: '14%',
        dataTestId: 'th-payment-status',
      },
      {
        id: 'actions',
        width: '0%',
      },
    ];
  }, [currentLang]);

  return (
    <ReusableTable
      isLoading={isLoading}
      isFetching={isFetching}
      tableHeadColumns={tableHeadColumns}
      totalItems={totalItems}
      totalPages={totalPages}
      paginationPage={page}
      dispatch={dispatch}
      sorting={sorting}
      data={
        data.map((el) => {
          const obj = { ...el } as IBaseInvoice;

          delete (obj as any)['client'];

          return obj;
        }) || []
      }
      customGridRow={({ row, index, table }) => (
        <InvoicesGridRow
          rowIndex={index}
          key={row.id}
          row={row}
          selected={table.selected.includes(row.id)}
          onEditRow={() => router.push(paths.dashboard.invoiceOverview(row.id, row.customer.id))}
        />
      )}
    />
  );
}
